/**
 * Base search form class
 */
class SearchForm extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');

    if (this.input) {
      this.input.form.addEventListener('reset', this.onFormReset.bind(this));
      this.input.addEventListener(
        'input',
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this)
      );
    }
  }

  toggleResetButton() {
    const resetIsHidden = this.resetButton?.classList.contains('hidden');
    if (this.resetButton) {
      if (this.input.value.length > 0 && resetIsHidden) {
        this.resetButton.classList.remove('hidden');
      } else if (this.input.value.length === 0 && !resetIsHidden) {
        this.resetButton.classList.add('hidden');
      }
    }
  }

  onChange() {
    this.toggleResetButton();
  }

  shouldResetForm() {
    return !document.querySelector('[aria-selected="true"] a');
  }

  onFormReset(event) {
    // Prevent default so the form reset doesn't set the value gotten from the url on page load
    event.preventDefault();
    // Don't reset if the user has selected an element on the predictive search dropdown
    if (this.shouldResetForm()) {
      this.input.value = '';
      this.input.focus();
      this.toggleResetButton();
    }
  }
}

/**
 * Utility function for debouncing
 */
function debounce(fn, wait) {
  let timer = null;
  return function(...args) {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      fn.apply(this, args);
    }, wait);
  };
}

/**
 * Register the base search form component
 */
customElements.define('search-form', SearchForm);

/**
 * Predictive search class that extends the base search form
 */
class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector("[data-predictive-search]");
    this.allPredictiveSearchInstances = document.querySelectorAll("predictive-search");
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = "";
    this.setupEventListeners();
  }

  setupEventListeners() {
    if (this.input && this.input.form) {
      this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));
      this.input.addEventListener("focus", this.onFocus.bind(this));
      this.addEventListener("focusout", this.onFocusOut.bind(this));
      this.addEventListener("keyup", this.onKeyup.bind(this));
      this.addEventListener("keydown", this.onKeydown.bind(this));
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    
    if ((!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm))) {
      const resultsWrapper = this.querySelector("#predictive-search-results-groups-wrapper");
      if (resultsWrapper) resultsWrapper.remove();
    }
    
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;
    
    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }
    
    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
    }
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = "";
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();
    if (currentSearchTerm.length) {
      if (this.searchTerm !== currentSearchTerm) {
        this.onChange();
      } else if (this.getAttribute("results") === "true") {
        this.open();
      } else {
        this.getSearchResults(this.searchTerm);
      }
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close();
      }
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.close(true);
    }
    
    event.preventDefault();
    
    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector("[data-predictive-search-search-for-text]");
    if (!searchForTextElement) return;
    
    const currentButtonText = searchForTextElement.innerText;
    if (!currentButtonText) return;
    
    // Check if previousTerm exists in the text before attempting to match
    if (previousTerm && currentButtonText.includes(previousTerm)) {
      const matches = currentButtonText.match(new RegExp(previousTerm, "g"));
      if (matches && matches.length > 1) return;
      
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    } else if (newTerm) {
      // If no previous term or it doesn't exist in the text, but we have a new term
      // We might want to update the text entirely or leave it as is
      // This depends on the expected behavior
    }
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) return;
    
    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button.predictive-search__item")
    ).filter(element => element.offsetParent !== null);
    
    let activeElementIndex = 0;
    
    if (moveUp && !selectedElement) return;
    
    let selectedElementIndex = -1;
    let i = 0;
    
    while (selectedElementIndex === -1 && i < allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }
    
    if (this.statusElement) {
      this.statusElement.textContent = "";
    }
    
    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }
    
    if (activeElementIndex === selectedElementIndex) return;
    
    const activeElement = allVisibleElements[activeElementIndex];
    if (activeElement) {
      activeElement.setAttribute("aria-selected", true);
      if (selectedElement) {
        selectedElement.setAttribute("aria-selected", false);
      }
      this.input.setAttribute("aria-activedescendant", activeElement.id);
    }
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
    if (selectedOption) {
      selectedOption.click();
    }
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    
    this.setLiveRegionLoadingState();
    
    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }
    
    // Check if routes is defined
    const searchUrl = typeof routes !== 'undefined' && routes.predictive_search_url 
      ? `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search` 
      : `/search/suggest?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`;
    
    fetch(searchUrl, {
      signal: this.abortController.signal
    })
    .then(response => {
      if (!response.ok) {
        const error = new Error(response.status);
        this.close();
        throw error;
      }
      return response.text();
    })
    .then(text => {
      const resultsMarkup = new DOMParser()
        .parseFromString(text, "text/html")
        .querySelector("#shopify-section-predictive-search")
        .innerHTML;
        
      this.allPredictiveSearchInstances.forEach(predictiveSearchInstance => {
        predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
      });
      
      this.renderSearchResults(resultsMarkup);
    })
    .catch(error => {
      if (error?.code !== 20) { // 20 is AbortError
        this.close();
        throw error;
      }
    });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText = this.loadingText || this.getAttribute("data-loading-text");
    
    if (this.statusElement && this.loadingText) {
      this.setLiveRegionText(this.loadingText);
    }
    
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;
    
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;
    
    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.innerHTML = resultsMarkup;
      this.setAttribute("results", true);
      this.setLiveRegionResults();
      this.open();
    }
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    const countElement = this.querySelector("[data-predictive-search-live-region-count-value]");
    if (countElement) {
      this.setLiveRegionText(countElement.textContent);
    }
  }

  getResultsMaxHeight() {
    const headerSection = document.querySelector(".section-header");
    if (headerSection) {
      this.resultsMaxHeight = window.innerHeight - headerSection.getBoundingClientRect().bottom;
    } else {
      this.resultsMaxHeight = window.innerHeight / 2; // Default fallback
    }
    return this.resultsMaxHeight;
  }

  open() {
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
      this.setAttribute("open", true);
      this.input.setAttribute("aria-expanded", true);
      this.isOpen = true;
    }
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
    }
    
    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) {
      selected.setAttribute("aria-selected", false);
    }
    
    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("loading");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
    this.resultsMaxHeight = false;
    
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.removeAttribute("style");
    }
  }
}

/**
 * Register the predictive search component
 */
customElements.define("predictive-search", PredictiveSearch);